import React, { useCallback, useRef, useState, forwardRef, useImperativeHandle, useEffect, useMemo } from 'react';
import { Box, Card, CardActionArea, CardContent, CardHeader, CardMedia, Chip, CircularProgress, Divider, FormControl, InputLabel, List, ListItem, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import BuilderSelct from '@/store/builderStoreSelector';
import { BuilderDesign, DesignMap, actions as builderActions } from '@/store/builderSlice';
import { useDispatch } from 'react-redux';
import Typography from '@mui/material/Typography';
import { isDesignLoaded, loadDesign } from '@/store/builder/thunks/builderDesignsLoader';
import ProductListView from './ProductListView';

const DesignListView = forwardRef((props: {
  roomType: string,
  styleName: string;
  closeExpandedViewCallback: (collapse: boolean) => void
}, ref) => {
  const dispatch = useDispatch();
  const [curDesign, setCurDesign] = useState<BuilderDesign | undefined>(undefined);
  const setDesignFromUrlParamRef = useRef(false);
  const roomName = props.roomType.toLowerCase();
  const styleName = props.styleName.toLowerCase();
  const designMap: DesignMap = BuilderSelct.designContent().designs;
  const urlParams: { [key: string]: string } = BuilderSelct.ui().urlParams;
  const curDesignId = BuilderSelct.ui().designId;
  let designs: BuilderDesign[] = [];

  const designLoaded = isDesignLoaded(roomName, styleName);

  designs = useMemo(() => {
    const result: BuilderDesign[] = [];
    if (designLoaded) {
      for (let roomName of Object.keys(designMap)) {
        if (roomName.toLowerCase() === props.roomType.toLowerCase()) {
          const curRoomDesigns = designMap[roomName];
          for (let vendorName of Object.keys(curRoomDesigns)) {
            const curVendorDesigns = curRoomDesigns[vendorName];
            for (let sName of Object.keys(curVendorDesigns)) {
              if (sName === styleName) {
                result.push(...curVendorDesigns[styleName]);
              }
            }
          }
        }
      }
    }
    return result;
  }, [designLoaded, designMap, props.roomType, styleName]);

  useEffect(() => {
    if (designLoaded && curDesignId && !curDesign) {
      const design = designs.find(design => parseInt(design.designId) === parseInt(curDesignId));
      if (design && !setDesignFromUrlParamRef.current) {
        dispatch(builderActions.updateProjectMetdata({
          metadata: { styleId: styleName, designId: design.designId, designImage: design?.designImage }
        }));
        setCurDesign(design);
        setDesignFromUrlParamRef.current = true;
      }
    }
  }, [designLoaded, curDesignId, designs, curDesign, dispatch, styleName]);

  if (!designLoaded) {
    dispatch(loadDesign({ roomName, styleName: styleName }) as any);
  }

  const counter = useRef(0);
  counter.current = counter.current + 1;

  const onDesignSelected = useCallback((designId: string) => () => {
    setCurDesign(designs.find(design => design.designId === designId));
  }, [designs]);

  const closeProductsView = useCallback(() => () => {
    setCurDesign(undefined);
  }, []);

  const closeExpandedView = () => {
    if (curDesign) {
      setCurDesign(undefined);
    }
    props.closeExpandedViewCallback(curDesign == null);
  };

  useImperativeHandle(ref, () => ({
    closeExpandedView,
  }));

  const designsList = () => {
    if (designs.length === 0) return "No designs";
    return (
      <List sx={{
        maxHeight: 555,
        overflow: 'auto',
        width: '100%',
        maxWidth: 360,
        bgcolor: 'background.paper',
        paddingTop: 0,
        paddingBottom: 0
      }}>
        {designs.filter((design, index, self) =>
          index === self.findIndex((d) => d.designId === design.designId)
        ).map((design: BuilderDesign) => (
          <ListItem key={design.designId} sx={{ paddingTop: 0, paddingBottom: '6px' }}>
            <Card key={design.designId} sx={{ m: 1, maxWidth: '100%', boxShadow: 'unset' }}>
              <Box component="div" onClick={onDesignSelected(design.designId)}>
                <CardHeader
                  sx={{ padding: '0px' }}
                  title={
                    <CardMedia
                      image={design.designImage}
                      component="img"
                      sx={{
                        ":hover": { borderColor: "primary.main", borderWidth: "4px", borderStyle: "solid" },
                        borderRadius: '12px',
                        cursor: 'pointer'
                      }}
                    />
                  }
                />
                <Typography variant="body2" sx={{ color: 'primary', paddingTop: '5px', lineHeight: '15px', textAlign: 'center' }}>
                  {design.designName}
                </Typography>
              </Box>
            </Card>
          </ListItem>
        ))}
      </List>
    );
  };

  const desingsView = () => {
    return designLoaded ? designsList() : <CircularProgress />;
  };

  const productsView = () => {
    return (
      <>
        <Card key={curDesign?.designId} sx={{ m: 1, maxWidth: '100%', boxShadow: 'unset' }}>
          <Box component="div">
            <CardHeader
              sx={{ padding: '0px' }}
              title={
                <CardMedia
                  image={curDesign?.designImage}
                  component="img"
                  sx={{ borderRadius: '12px' }}
                />
              }
            />
            <Typography variant="body2" sx={{
              color: 'primary',
              paddingTop: '5px',
              lineHeight: '15px',
              textAlign: 'center',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
            }}>
              {curDesign?.designName}
            </Typography>
          </Box>
        </Card>
        <Divider sx={{ borderColor: 'gray', borderWidth: '1px', marginRight: '20px', marginLeft: '20px' }} />
        <ProductListView
          products={curDesign!.products}
          closeProductsView={!setDesignFromUrlParamRef.current ? closeProductsView : undefined}
          maxHeight={373}
        />
      </>
    );
  };

  return (
    <div>
      {curDesign ? productsView() : desingsView()}
    </div>
  );
});

DesignListView.displayName = 'DesignListView';
export default DesignListView;